import React, { useEffect, useMemo, useRef, useState } from 'react';
import EmptyState from '../common/EmptyState.jsx';
import InlineBanner from '../common/InlineBanner.jsx';
import { IconCheck, IconCopy, IconDownload, IconSearch, IconSparkle } from './icons.jsx';
import '../../css/TranscriptPanel.css';

/** Split `text` around every case-insensitive occurrence of `query`. */
function segment(text, query) {
    if (!query) return [{ text, match: false }];
    const lower = text.toLowerCase();
    const needle = query.toLowerCase();
    const parts = [];
    let i = 0;
    while (i < text.length) {
        const at = lower.indexOf(needle, i);
        if (at === -1) { parts.push({ text: text.slice(i), match: false }); break; }
        if (at > i) parts.push({ text: text.slice(i, at), match: false });
        parts.push({ text: text.slice(at, at + needle.length), match: true });
        i = at + needle.length;
    }
    return parts;
}

const wordCount = (text) => (text.trim() ? text.trim().split(/\s+/).length : 0);

/**
 * The transcript / AI description for one file.
 *
 * A transcript is the one piece of a file's record people actually read rather
 * than scan, so it gets the affordances reading needs — find-in-text with match
 * stepping, copy, and a .txt export — instead of a fixed-height box you can only
 * scroll. The run button carries its own state because Whisper takes a while and
 * a button that silently does nothing for 40 seconds reads as broken.
 *
 * Props:
 *   text        – the transcript, or empty/absent
 *   isImage     – images get a vision DESCRIPTION, not a transcript; the copy changes
 *   supported   – false for file types the transcriber ignores (anything not a/v/image)
 *   fileName    – used for the exported .txt filename
 *   canRun      – role allows spending an AI call
 *   onRun       – () => Promise, requests transcription
 */
const TranscriptPanel = ({ text = '', isImage = false, supported = true, fileName = 'file', canRun = true, onRun }) => {
    const noun = isImage ? 'description' : 'transcript';
    const Noun = isImage ? 'Description' : 'Transcript';

    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);
    const markRefs = useRef([]);
    const bodyRef = useRef(null);

    const parts = useMemo(() => segment(text, query.trim()), [text, query]);
    const matchCount = parts.filter((p) => p.match).length;

    useEffect(() => { setActive(0); }, [query]);

    useEffect(() => {
        if (matchCount > 0) markRefs.current[active]?.scrollIntoView({ block: 'center' });
    }, [active, matchCount, query]);

    useEffect(() => {
        if (!copied) return undefined;
        const t = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(t);
    }, [copied]);

    const step = (delta) => {
        if (matchCount === 0) return;
        setActive((i) => (i + delta + matchCount) % matchCount);
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
        } catch (err) {
            console.error('Could not copy the transcript:', err);
        }
    };

    const handleDownload = () => {
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${String(fileName).replace(/\.[^.]+$/, '') || 'transcript'}.txt`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const handleRun = async () => {
        setRunning(true);
        setError(null);
        try {
            await onRun?.();
        } catch (err) {
            console.error(`Failed to generate the ${noun}:`, err);
            setError(`The ${noun} could not be generated. Please try again.`);
        } finally {
            setRunning(false);
        }
    };

    if (!supported) {
        return (
            <EmptyState
                compact
                icon={<IconSparkle width="24" height="24" />}
                title={`No ${noun} for this file type`}
                description="Transcription covers audio and video; images get an AI description instead."
            />
        );
    }

    if (!text) {
        return (
            <div className="tp-root">
                {error && (
                    <InlineBanner variant="danger" onDismiss={() => setError(null)}>{error}</InlineBanner>
                )}
                {running ? (
                    <EmptyState
                        compact
                        icon={<span className="tp-spinner" />}
                        title={isImage ? 'Describing this image…' : 'Transcribing…'}
                        description="This runs on the server and can take a minute for long recordings. You can keep working — reopen this tab to check."
                    />
                ) : (
                    <EmptyState
                        compact
                        icon={<IconSparkle width="24" height="24" />}
                        title={`No ${noun} yet`}
                        description={
                            canRun
                                ? isImage
                                    ? 'Generate an AI description of what this image shows. It becomes searchable alongside the file’s tags.'
                                    : 'Run speech-to-text on this file. The result is stored on the file and becomes searchable.'
                                : `Ask a workspace editor to generate the ${noun} for this file.`
                        }
                        action={
                            canRun && (
                                <button type="button" className="qd-btn qd-btn--primary qd-btn--sm" onClick={handleRun}>
                                    <IconSparkle />
                                    {isImage ? 'Describe image' : 'Run transcription'}
                                </button>
                            )
                        }
                    />
                )}
            </div>
        );
    }

    let markIndex = -1;

    return (
        <div className="tp-root tp-root--filled">
            <div className="tp-toolbar">
                <div className="tp-find">
                    <IconSearch className="tp-find-icon" />
                    <input
                        type="search"
                        className="tp-find-input"
                        value={query}
                        placeholder={`Find in ${noun}…`}
                        aria-label={`Find in ${noun}`}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); step(e.shiftKey ? -1 : 1); }
                        }}
                    />
                    {query.trim() && (
                        <span className="tp-find-nav">
                            <span className="tp-find-count" aria-live="polite">
                                {matchCount === 0 ? 'No matches' : `${active + 1}/${matchCount}`}
                            </span>
                            <button type="button" className="tp-find-step" onClick={() => step(-1)}
                                    disabled={matchCount === 0} aria-label="Previous match">↑</button>
                            <button type="button" className="tp-find-step" onClick={() => step(1)}
                                    disabled={matchCount === 0} aria-label="Next match">↓</button>
                        </span>
                    )}
                </div>

                <div className="tp-tools">
                    <button type="button" className="qd-btn qd-btn--ghost qd-btn--sm" onClick={handleCopy}
                            title={`Copy the ${noun}`}>
                        {copied ? <IconCheck /> : <IconCopy />}
                        {copied ? 'Copied' : 'Copy'}
                    </button>
                    <button type="button" className="qd-btn qd-btn--ghost qd-btn--sm" onClick={handleDownload}
                            title="Download as a .txt file">
                        <IconDownload />
                        .txt
                    </button>
                </div>
            </div>

            <div className="tp-body" ref={bodyRef} tabIndex={0}>
                <p className="tp-text">
                    {parts.map((part, i) => {
                        if (!part.match) return <React.Fragment key={i}>{part.text}</React.Fragment>;
                        markIndex += 1;
                        const at = markIndex;
                        return (
                            <mark
                                key={i}
                                ref={(el) => { markRefs.current[at] = el; }}
                                className={`tp-mark ${at === active ? 'is-active' : ''}`.trim()}
                            >
                                {part.text}
                            </mark>
                        );
                    })}
                </p>
            </div>

            <div className="tp-footer">
                <span>{Noun} · {wordCount(text).toLocaleString()} words</span>
                <span className="tp-footer-note">Stored on the file and indexed for search.</span>
            </div>
        </div>
    );
};

export default TranscriptPanel;
